// Paket A8b — einmalige, ruhige In-App-Frage zum wöchentlichen E-Mail-Rückblick.
//
// ⚠️ KEIN TEIL DES MITGLIEDSCHAFTS-PROMO-SYSTEMS (PromoProvider, MembershipPromoHost,
// membership-promo.js). Eigener Endpunkt (api/emailMarketing.ts), eigener
// Server-Zustand (Tabelle email_marketing_prompt), eigene Ruhe-Regeln, keine
// Sitzungs-Caps/Cooldowns des Promo-Systems. Die Mitgliedschafts-Promo-Logik bleibt
// unberührt.
//
// Wann erscheint die Frage?
//   • Server: registriertes Konto mit E-Mail-Adresse, belegbar ≥ 16 Jahre alt (Geburts-
//     datum, DSGVO Art. 8 DE), Einwilligungstexte in der Sprache des Kontos vorhanden,
//     ≥ 5 eigene Abenteuer-Züge, noch nie gefragt, noch nicht eingewilligt/abgemeldet.
//     Der Server vergibt die Frage atomar (POST /prompt/claim); nur wer `claimed: true`
//     bekommt, zeigt sie an → höchstens einmal je Konto. Nennt der Server einen
//     ENDGÜLTIGEN Grund (`final`: schon gefragt, zu jung, bereits bestätigt …), hört der
//     Client in dieser Sitzung auf nachzufragen; weiter gefragt wird nur bei „noch nicht
//     genug Züge" und „Bestätigung ausstehend".
//   • Client (nur der Zeitpunkt): nicht in den ersten 2 Minuten der Sitzung, kein
//     Startup-Gate, keine ruhige Fläche (Einstellungen, Kauf, Anmeldung …), kein
//     offener Dialog, keine andere angedockte Karte, kein angefangener Text im
//     Eingabefeld.
//
// Darstellung: eine ruhige, schließbare Karte am unteren Rand (wie die Hinweiskarte
// „Bestandsschutz endet" und die Klangbühnen-Leiste). KEIN Modal, kein Scrim, keine
// Animation, kein Countdown.
//
// Antworten (alle serverseitig gespeichert, danach nie wieder gefragt):
//   „Ja, gerne"  → startet NUR das Double-Opt-in (Bestätigungsmail). Die Einwilligung
//                  entsteht erst mit dem Klick auf den Link in der Mail.
//   „Nein danke" → merkt sich die Absage.
//   X            → schließt; zählt als beantwortet (einmalig, nicht „später nochmal").
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  answerEmailMarketingPrompt,
  claimEmailMarketingPrompt,
  getEmailMarketingPromptState,
  type EmailMarketingPromptAnswer,
} from '../api/emailMarketing';
import { useAuth } from '../auth/AuthProvider';
import { isGuestUser } from '../auth/guest';
import { Button, Icon, LegalDialog, useToast } from '../components/ui';
import { useT } from '../i18n';
import { useStartupGate, type StartupGate } from './startupGateContext';
import styles from './EmailMarketingPromptHost.module.css';

export const EMAIL_PROMPT_QUERY_KEY = ['email-marketing-prompt'] as const;

/** „nicht in den ersten 2 Minuten der Sitzung". */
export const EMAIL_PROMPT_MIN_SESSION_MS = 2 * 60_000;
/** So oft wird die Berechtigung beim Server nachgefragt, solange sie fehlt UND nicht endgültig ist (≥ 5 Züge kommen erst im Spiel). */
const ELIGIBILITY_POLL_MS = 2 * 60_000;
/** So oft werden die Ruhe-Bedingungen neu geprüft, solange die Frage wartet. */
const QUIET_RECHECK_MS = 4_000;
/** Nach einem fehlgeschlagenen Claim (offline, Serverfehler) erst später erneut versuchen. */
const CLAIM_RETRY_MS = 30_000;

// Flächen, auf denen die Frage stören oder doppelt wäre: die Einstellungen (dort
// steht der Schalter ohnehin), Mitgliedschaft/Kauf, Anmeldung, Einführung, Rechtstexte,
// Admin, Sofortstart.
const QUIET_ROUTES: readonly RegExp[] = [
  /^\/settings(\/|\?|$)/,
  /^\/mitgliedschaft(\/|\?|$)/,
  /^\/coins(\/|\?|$)/,
  /^\/login(\/|\?|$)/,
  /^\/onboarding(\/|\?|$)/,
  /^\/legal(\/|\?|$)/,
  /^\/admin(\/|\?|$)/,
  /^\/first-scene(\/|\?|$)/,
  /^\/start(\/|\?|$)/,
];

export function isQuietRoute(pathname: string): boolean {
  return QUIET_ROUTES.some((pattern) => pattern.test(pathname));
}

export type EmailPromptVerdict =
  | 'ok'
  | 'session_young'
  | 'startup_gate'
  | 'quiet_route'
  | 'dialog_open'
  | 'other_dock'
  | 'draft';

export interface EmailPromptMoment {
  nowMs: number;
  sessionStartedAtMs: number;
  gate: StartupGate;
  pathname: string;
  /** Ein Dialog liegt über der Seite (Modal, Sheet, Bestätigung …). */
  dialogOpen: boolean;
  /** Eine andere angedockte Hinweiskarte ist sichtbar. */
  otherDockVisible: boolean;
  /** Im Eingabefeld steht ein angefangener Text. */
  draftActive: boolean;
}

/** Die reine Zeitpunkt-Regel (ohne DOM, Uhr und Netz testbar). */
export function evaluateEmailPromptMoment(moment: EmailPromptMoment): EmailPromptVerdict {
  if (moment.nowMs - moment.sessionStartedAtMs < EMAIL_PROMPT_MIN_SESSION_MS) return 'session_young';
  if (moment.gate !== 'none') return 'startup_gate';
  if (isQuietRoute(moment.pathname)) return 'quiet_route';
  if (moment.dialogOpen) return 'dialog_open';
  if (moment.otherDockVisible) return 'other_dock';
  if (moment.draftActive) return 'draft';
  return 'ok';
}

const OWN_DOCK = 'email-marketing-prompt';

function readDomMoment(): Pick<EmailPromptMoment, 'dialogOpen' | 'otherDockVisible' | 'draftActive'> {
  if (typeof document === 'undefined') return { dialogOpen: false, otherDockVisible: false, draftActive: false };
  const dialogOpen = document.querySelector('dialog[open], [role="dialog"], [aria-modal="true"]') !== null;
  const otherDockVisible = document.querySelector(`[data-lorath-dock]:not([data-lorath-dock="${OWN_DOCK}"])`) !== null;
  const active = document.activeElement as HTMLElement | null;
  let draftActive = false;
  if (active instanceof HTMLTextAreaElement) draftActive = active.value.trim().length > 0;
  else if (active instanceof HTMLInputElement) {
    draftActive = ['text', 'search', 'email', 'url', 'tel', ''].includes(active.type) && active.value.trim().length > 0;
  } else if (active?.isContentEditable) draftActive = (active.textContent ?? '').trim().length > 0;
  return { dialogOpen, otherDockVisible, draftActive };
}

export interface EmailMarketingPromptCardProps {
  onAnswer: (answer: EmailMarketingPromptAnswer) => void;
}

/** Die Karte selbst (reine Darstellung). */
export function EmailMarketingPromptCard({ onAnswer }: EmailMarketingPromptCardProps) {
  const { t } = useT();
  // Die Datenschutzhinweise öffnen als Overlay (wie AiNotice) — der laufende Chat bleibt stehen.
  const [privacyOpen, setPrivacyOpen] = useState(false);
  return (
    <div className={styles.dock} data-lorath-dock={OWN_DOCK} data-testid="email-marketing-prompt">
      <section className={styles.card} role="region" aria-label={t('email_prompt.label')}>
        <p className={styles.text}>{t('email_prompt.text')}</p>
        <p className={styles.privacy}>
          <button
            type="button"
            className={styles.privacyLink}
            data-testid="email-marketing-prompt-privacy"
            onClick={() => setPrivacyOpen(true)}
          >
            {t('settings.notifications.email_marketing.privacy_link')}
          </button>
        </p>
        <div className={styles.actions}>
          <Button size="sm" data-testid="email-marketing-prompt-yes" onClick={() => onAnswer('yes')}>
            {t('email_prompt.yes')}
          </Button>
          <Button size="sm" variant="ghost" data-testid="email-marketing-prompt-no" onClick={() => onAnswer('no')}>
            {t('email_prompt.no')}
          </Button>
        </div>
        <button
          type="button"
          className={styles.close}
          aria-label={t('common.close')}
          data-testid="email-marketing-prompt-close"
          onClick={() => onAnswer('dismiss')}
        >
          <Icon name="close" size={18} />
        </button>
      </section>
      <LegalDialog doc={privacyOpen ? 'datenschutz' : null} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}

export function EmailMarketingPromptHost() {
  const auth = useAuth();
  const gate = useStartupGate();
  const location = useLocation();
  const { toast } = useToast();

  const userId = auth.user?.userId ?? null;
  const hasEmail = typeof auth.user?.email === 'string' && auth.user.email.trim().length > 0;
  const enabled = auth.status === 'authed' && !isGuestUser(auth.user ?? null) && hasEmail;

  const sessionStartedAtRef = useRef(Date.now());
  const claimedForRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const [shown, setShown] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Kontowechsel: nichts vom vorigen Konto stehen lassen.
  useEffect(() => {
    claimedForRef.current = null;
    setShown(false);
  }, [userId]);

  // Der Server sagt, ob das Konto die Frage bekommen DARF (Alter, Züge, nie gefragt …).
  // Solange nicht, fragen wir sparsam nach — die fünf Züge entstehen erst im Spiel.
  const eligibility = useQuery({
    queryKey: [...EMAIL_PROMPT_QUERY_KEY, userId],
    queryFn: getEmailMarketingPromptState,
    enabled,
    staleTime: 60_000,
    retry: 0,
    // Berechtigt oder endgültig verneint → keine weiteren Abfragen in dieser Sitzung.
    refetchInterval: (query) => (query.state.data?.eligible === true || query.state.data?.final === true ? false : ELIGIBILITY_POLL_MS),
  });
  const eligible = eligibility.data?.eligible === true;

  const pathname = location.pathname;

  // Den richtigen Moment abwarten, dann die Frage beim Server beanspruchen.
  useEffect(() => {
    if (!enabled || !eligible || shown) return undefined;
    const tryClaim = (): boolean => {
      if (claimedForRef.current === userId) return true;
      const verdict = evaluateEmailPromptMoment({
        nowMs: Date.now(),
        sessionStartedAtMs: sessionStartedAtRef.current,
        gate,
        pathname,
        ...readDomMoment(),
      });
      if (verdict !== 'ok') return false;
      claimedForRef.current = userId;
      void claimEmailMarketingPrompt()
        .then((claimed) => { if (claimed && mountedRef.current) setShown(true); })
        .catch(() => {
          // Offline oder Serverfehler: nicht beansprucht → später erneut versuchen.
          if (claimedForRef.current !== userId) return;
          claimedForRef.current = null;
          window.setTimeout(() => { if (mountedRef.current) setRetryTick((tick) => tick + 1); }, CLAIM_RETRY_MS);
        });
      return true;
    };
    if (tryClaim()) return undefined;
    const timer = window.setInterval(() => {
      if (tryClaim()) window.clearInterval(timer);
    }, QUIET_RECHECK_MS);
    return () => window.clearInterval(timer);
  }, [enabled, eligible, shown, userId, gate, pathname, retryTick]);

  const onAnswer = useCallback((answer: EmailMarketingPromptAnswer) => {
    setShown(false);
    void answerEmailMarketingPrompt(answer)
      .then((result) => {
        if (answer !== 'yes' || !mountedRef.current) return;
        if (result.sent || result.status === 'pending') toast({ tone: 'success', titleKey: 'email_prompt.sent' });
        else toast({ tone: 'error', titleKey: 'email_prompt.failed' });
      })
      .catch(() => {
        if (answer === 'yes') toast({ tone: 'error', titleKey: 'email_prompt.failed' });
      });
  }, [toast]);

  if (!shown || gate !== 'none' || isQuietRoute(pathname)) return null;
  return <EmailMarketingPromptCard onAnswer={onAnswer} />;
}
